<template>
    <div class="floating-window"
         :style="{ top: position.y + 'px', left: position.x + 'px', width: size.width + 'px', height: size.height + 'px' }"
         @mousedown="startDragging">
        <div class="window-header">{{ title ?? '' }}
            <QBtn size="sm" flat round dense color="primary"
               icon="mdi-close-box-outline"
               @click="$emit('close')"
               >
            </QBtn>
        </div>
        <div class="window-content">
            <slot>
                <pre class="pre-content" style="overflow: auto;">{{content ?? 'Nothing'}}</pre>
            </slot>
        </div>
        <div class="resize-handle se" @mousedown.stop="startResizing('se', $event)"></div>
        <div class="resize-handle sw" @mousedown.stop="startResizing('sw', $event)"></div>
        <div class="resize-handle ne" @mousedown.stop="startResizing('ne', $event)"></div>
        <div class="resize-handle nw" @mousedown.stop="startResizing('nw', $event)"></div>
    </div>
</template>

<script>
    import { QBtn } from 'quasar';
    import { common } from './helpers.js';

    export default {
        name: 'FloatingWindow',
        components: { QBtn },
        emits: ['close'],
        props: {
            title: null,
            initialX: { type: Number, default: 100 },
            initialY: { type: Number, default: 100 },
            initialWidth: { type: Number, default: 300 },
            initialHeight: { type: Number, default: 200 },
            minWidth: { type: Number, default: 100 },
            minHeight: { type: Number, default: 100 },
            content: null,
        },
        data() {
            return {
                position: { x: this.initialX, y: this.initialY },
                size: { width: this.initialWidth, height: this.initialHeight },
                isDragging: false,
                isResizing: false,
                resizeDirection: '',
                startPos: { x: 0, y: 0 },
                startSize: { width: 0, height: 0 },
                startMouse: { x: 0, y: 0 },
            };
        },
        created() {
            const settings = common.getGlobalSetting('floatingwindow');
            if (settings) {
                this.position.x = settings.x;
                this.position.y = settings.y;
                this.size.width = settings.w;
                this.size.height = settings.h;
            }
        },
        methods: {
            startDragging(event) {
                if (event.target.classList.contains('window-header')) {
                    this.isDragging = true;
                    this.startMouse = { x: event.clientX, y: event.clientY };
                    this.startPos = { x: this.position.x, y: this.position.y };
                    document.addEventListener('mousemove', this.handleDragging);
                    document.addEventListener('mouseup', this.stopDragging);
                }
            },
            handleDragging(event) {
                if (this.isDragging) {
                    const dx = event.clientX - this.startMouse.x;
                    const dy = event.clientY - this.startMouse.y;
                    this.position.x = Math.max(0, this.startPos.x + dx);
                    this.position.y = Math.max(0, this.startPos.y + dy);
                }
            },
            stopDragging() {
                this.isDragging = false;
                document.removeEventListener('mousemove', this.handleDragging);
                document.removeEventListener('mouseup', this.stopDragging);

                this.save();
            },
            startResizing(direction, event) {
                this.isResizing = true;
                this.resizeDirection = direction;
                this.startSize = { width: this.size.width, height: this.size.height };
                this.startMouse = { x: event.clientX, y: event.clientY };
                this.startPos = { x: this.position.x, y: this.position.y };
                document.addEventListener('mousemove', this.handleResizing);
                document.addEventListener('mouseup', this.stopResizing);
            },
            handleResizing(event) {
                if (!this.isResizing) return;
                const dx = event.clientX - this.startMouse.x;
                const dy = event.clientY - this.startMouse.y;

                if (this.resizeDirection.includes('e')) {
                    this.size.width = Math.max(this.minWidth, this.startSize.width + dx);
                }
                if (this.resizeDirection.includes('w')) {
                    this.size.width = Math.max(this.minWidth, this.startSize.width - dx);
                    this.position.x = this.startPos.x + dx;
                }
                if (this.resizeDirection.includes('s')) {
                    this.size.height = Math.max(this.minHeight, this.startSize.height + dy);
                }
                if (this.resizeDirection.includes('n')) {
                    this.size.height = Math.max(this.minHeight, this.startSize.height - dy);
                    this.position.y = this.startPos.y + dy;
                }
            },
            stopResizing() {
                this.isResizing = false;
                this.resizeDirection = '';
                document.removeEventListener('mousemove', this.handleResizing);
                document.removeEventListener('mouseup', this.stopResizing);

                this.save();
            },
            save() {
                common.saveGlobalSetting('floatingwindow', { x: this.position.x, y: this.position.y, w: this.size.width, h: this.size.height });
            },
        },
    };
</script>

<style scoped>
    .floating-window {
        position: absolute;
        background: white;
        border: 1px solid #ccc;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        user-select: none;
        pointer-events: auto;
        z-index: 999;
        display: flex;
        flex-direction: column;
    }

    .window-header {
        background: #f0f0f0;
        padding: 5px;
        cursor: move;
        font-weight: bold;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }

    .window-content {
        padding: 10px;
        display: flex;
        flex-direction: column;
        flex-grow: 1;
    }

    .pre-content{
        flex-grow: 1;
    }

    .resize-handle {
        position: absolute;
        width: 10px;
        height: 10px;
        background: #ccc;
    }

        .resize-handle:hover {
            background: #999;
        }

    .se {
        bottom: -5px;
        right: -5px;
        cursor: se-resize;
    }

    .sw {
        bottom: -5px;
        left: -5px;
        cursor: sw-resize;
    }

    .ne {
        top: -5px;
        right: -5px;
        cursor: ne-resize;
    }

    .nw {
        top: -5px;
        left: -5px;
        cursor: nw-resize;
    }
</style>
